import { GEO_TIMEOUT_MS } from '@mai-team-amusement/core';
import type { Position } from '@mai-work-clock/contracts';
import { useEffect, useState } from 'react';

/** Why the position could not be read; each has its own guidance in the UI. */
export const GeoFailure = {
  Denied: 'denied',
  Unavailable: 'unavailable',
  Timeout: 'timeout',
  Unsupported: 'unsupported',
} as const;

export type GeoFailure = (typeof GeoFailure)[keyof typeof GeoFailure];

export class GeolocationFailure extends Error {
  readonly failure: GeoFailure;

  constructor(failure: GeoFailure) {
    super(`geolocation ${failure}`);
    this.name = 'GeolocationFailure';
    this.failure = failure;
  }
}

function failureOf(error: GeolocationPositionError): GeoFailure {
  if (error.code === error.PERMISSION_DENIED) {
    return GeoFailure.Denied;
  }
  return error.code === error.TIMEOUT ? GeoFailure.Timeout : GeoFailure.Unavailable;
}

/** A fresh, high-accuracy position for a punch (never a cached one). */
export function currentPosition(): Promise<Position> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      reject(new GeolocationFailure(GeoFailure.Unsupported));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyM: position.coords.accuracy,
        });
      },
      (error) => {
        reject(new GeolocationFailure(failureOf(error)));
      },
      { enableHighAccuracy: true, timeout: GEO_TIMEOUT_MS, maximumAge: 0 },
    );
  });
}

export const LocationPermission = {
  Granted: 'granted',
  Prompt: 'prompt',
  Denied: 'denied',
  Unsupported: 'unsupported',
  Unknown: 'unknown',
} as const;

export type LocationPermission = (typeof LocationPermission)[keyof typeof LocationPermission];

/** The browser's location permission, kept current (Permissions API, where available). */
export function useLocationPermission(): LocationPermission {
  const [state, setState] = useState<LocationPermission>(() =>
    typeof navigator !== 'undefined' && 'geolocation' in navigator
      ? LocationPermission.Unknown
      : LocationPermission.Unsupported,
  );
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('permissions' in navigator)) {
      return undefined;
    }
    let status: PermissionStatus | null = null;
    let active = true;
    const update = () => {
      if (status !== null && active) {
        setState(status.state);
      }
    };
    navigator.permissions
      .query({ name: 'geolocation' })
      .then((result) => {
        status = result;
        update();
        result.addEventListener('change', update);
      })
      .catch(() => undefined);
    return () => {
      active = false;
      status?.removeEventListener('change', update);
    };
  }, []);
  return state;
}
